import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createCheckoutSession,
  getCurrentSubscription,
  getNewPlansInitially,
} from "components/subscription/service";
import {
  selectCurrentSubscription,
  setProductPlan,
  updateCurrentSubscription,
} from "components/subscription/subscriptionSlice";
import { mixpanelCustomEvent } from "components/mixpanel/eventTriggers";
import { MixpanelEventName } from "components/mixpanel/types";
import { calendalyInvite } from "components/utils/network/endpoints";
import { useUser } from "@clerk/clerk-react";
import { setDomainSettingsDetails } from "components/domain-settings/domainSettingsSlice";
import { getDomainSettings } from "components/domain-settings/service";
import { SUBSCRIPTION_DISPLAY_NAME, SUBSCRIPTION_STATUS } from "components/subscription/types";

export function useSubscription() {
  const dispatch = useDispatch();
  const { isSignedIn } = useUser();

  const [newPlans, setNewPlans] = useState<any>(null);
  const [createNewSubscription, setCreateNewSubscription] = useState(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [showDomainSettingsModal, setShowDomainSettingsModal] = useState(false);

  const currentSubscription = useSelector(selectCurrentSubscription);

  const fetchPlans = useCallback(async () => {
    if (!isSignedIn) {
      return;
    }
    try {
      setIsSubscriptionLoading(true);
      const plans = await getNewPlansInitially();
      setNewPlans(plans);
      dispatch(setProductPlan(plans));
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setIsSubscriptionLoading(false);
    }
  }, [isSignedIn, dispatch]);

  const fetchCurrentSubscription = useCallback(
    async (showLoader = false) => {
      if (!isSignedIn) {
        return;
      }
      try {
        if (showLoader) {
          setIsSubscriptionLoading(true);
        }

        const currentSubscription = await getCurrentSubscription();
        await fetchPlans();
        dispatch(updateCurrentSubscription(currentSubscription));
        setCreateNewSubscription(false);

        const domainSettings = await getDomainSettings();

        if (
          Array.isArray(domainSettings) &&
          (currentSubscription.status === SUBSCRIPTION_STATUS.ACTIVE ||
            currentSubscription.status === SUBSCRIPTION_STATUS.TRAILING)
        ) {
          dispatch(setDomainSettingsDetails(domainSettings));
          setShowDomainSettingsModal(domainSettings.length === 0);
        } else {
          setShowDomainSettingsModal(false);
        }
      } catch (error: any) {
        console.error("Error fetching subscriptions:", error);

        const urlParams = new URLSearchParams(window.location.search);
        const redirectFromCheckout =
          urlParams.get("redirectFromCheckout") === "true";

        if (error.status === 404 && !redirectFromCheckout) {
          await fetchPlans();
          setCreateNewSubscription(true);
          localStorage.setItem("newUser", "true");
        }
      } finally {
        setIsSubscriptionLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSignedIn, fetchPlans, dispatch]
  );

  const handleSubscriptionChoosen = useCallback(
    async (plan: any, userData: any) => {
      if (!isSignedIn) {
        return;
      }
      setIsSubscriptionLoading(true);

      const mixpanelProps = {
        $name: userData.name,
        $email: userData.email,
      };

      mixpanelCustomEvent({
        mixpanelProps,
        id: userData.uid.toString(),
        eventName: MixpanelEventName.userSelctedPlan,
      });
      try {
        const isFreeTrial = plan.displayName !== SUBSCRIPTION_DISPLAY_NAME.EMERGING;
        const checkout = await createCheckoutSession(plan.productPricingId, isFreeTrial);
        if (checkout) {
          window.location.href = checkout.url;
        }
      } catch (error) {
        console.error("Error creating checkout session:", error);
      } finally {
        setIsSubscriptionLoading(false);
      }
    },
    [isSignedIn]
  );

  const handleCalendlyInvite = useCallback(
    (userData: any) => {
      if (!isSignedIn) {
        return;
      }

      const mixpanelProps = {
        $name: userData.name,
        $email: userData.email,
      };

      mixpanelCustomEvent({
        mixpanelProps,
        id: userData.uid.toString(),
        eventName: MixpanelEventName.userClickedOnGetEarlyAccess,
      });

      window.location.href = calendalyInvite;
    },
    [isSignedIn]
  );

  return {
    newPlans,
    createNewSubscription,
    showDomainSettingsModal,
    setShowDomainSettingsModal,
    setCreateNewSubscription,
    isSubscriptionLoading,
    currentSubscription,
    fetchPlans,
    fetchCurrentSubscription,
    handleSubscriptionChoosen,
    handleCalendlyInvite,
  };
}
